import { makeAutoObservable } from 'mobx'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import Link from '@mui/icons-material/Link'
import InsertDriveFile from '@mui/icons-material/InsertDriveFile'
import { decodeFile } from 'reusable/utils/file-operations'
import { notNull } from '@gap/utils/utils'
import { apiClient } from 'api/trpc/trpc-client'
import Loader from 'reusable/mobx-helpers/Loader'
import { ProposalAttachment } from '@gap/backend/app-services/proposal/models/ProposalStep5Dto'
import { ProposalDto } from '@gap/backend/app-services/proposal/models/ProposalDto'

export interface ProposalAttachmentStoreProps {
   attachment: ProposalAttachment
   proposal: ProposalDto
   canDelete: boolean
   onDelete?: (attachment: ProposalAttachment) => Promise<void> | void
}

type AttachmentType = 'file' | 'link'

export default class ProposalAttachmentStore {
   sp: ProposalAttachmentStoreProps
   file: ReturnType<typeof decodeFile> | undefined
   link: URL | undefined
   deleteLoader = new Loader()

   constructor(sp: ProposalAttachmentStoreProps) {
      makeAutoObservable(this)
      this.sp = sp

      if (this.attachmentType === 'file') {
         this.file = decodeFile(notNull(sp.attachment.fileName))
      } else {
         try {
            this.link = new URL(notNull(sp.attachment.link))
         } catch {
            // Tolerated — older proposals may carry a malformed link; allow delete
         }
      }
   }

   get attachmentType(): AttachmentType {
      return this.sp.attachment.fileName ? 'file' : 'link'
   }

   get attachmentIcon() {
      return this.attachmentType === 'file' ? InsertDriveFile : Link
   }

   get primaryText() {
      if (this.attachmentType === 'file') {
         return this.file?.fileName
      }
      return this.sp.attachment.linkName
   }

   get secondaryText() {
      if (this.attachmentType === 'file') {
         return this.file?.extension.toUpperCase()
      }
      return this.link?.origin
   }

   get secondaryTooltipText() {
      if (this.attachmentType === 'file') {
         return this.file?.extension
      }
      return this.link?.href
   }

   get canDelete() {
      return this.sp.canDelete
   }

   onAttachmentIconClick = async () => {
      if (this.canDelete) {
         return undefined
      }
      if (this.attachmentType === 'file') {
         const downloadUrl = await apiClient.proposals.getAttachmentDownloadUrl.query({
            proposalId: this.sp.proposal.id,
            fileName: notNull(this.sp.attachment.fileName),
            advisorOrgId: this.sp.proposal.advisorOrgId,
         })
         window.open(downloadUrl, '_blank')
      } else {
         window.open(this.sp.attachment.link, '_blank')
      }
   }

   onDeleteClick = async () => {
      if (!this.sp.onDelete) return
      await this.deleteLoader.executeLoading(async () => {
         await this.sp.onDelete?.(this.sp.attachment)
      })
   }
}

export const { Provider: ProposalAttachmentStoreProvider, useProvidedStore: useProposalAttachmentStore } =
   createStoreContext(ProposalAttachmentStore)
